import type { ComponentProps } from 'react';
import { useCallback, useLayoutEffect } from 'react';
import type { Modal } from 'react-native';

import { useModalMachine } from '../../modal-machine';

/**
 * Wrapper around the Modal Machine integration.
 */
export function useModalMachineIntegration(
  props: ModalMachineIntegrationProps,
) {
  const {
    visible,
    animationType,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  } = props;

  const modalMachineInitialState = visible ? 'shown' : 'dismissed';
  const { helpers: modalServiceHelpers } = useModalMachine({
    initial: modalMachineInitialState,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  });

  const syncAnimationState = useCallback(() => {
    if (animationType === 'none') return;

    if (visible) {
      modalServiceHelpers.sendStartShowEvent();

      return;
    }

    modalServiceHelpers.sendStartDismissEvent();
  }, [animationType, modalServiceHelpers, visible]);

  useLayoutEffect(syncAnimationState, [syncAnimationState]);

  return { modalServiceHelpers };
}

// ─── Types ─────────────────────────────────────────────────────────────

type ModalMachineIntegrationProps = Readonly<{
  visible?: boolean;
  animationType: ComponentProps<typeof Modal>['animationType'];
  onShowing?: () => void;
  onShown?: () => void;
  onDismissing?: () => void;
  onDismissed?: () => void;
}>;
